/**
 * **One occurrence, sorted into the four bands the pane draws.** Canon:
 * `docs/design/ankiety-klienta-2026-09-16/szkice/kierunek-a.html`, as the owner refined it on
 * 16 September 2026 — „dopracowana forma", sections rather than a numbered list of rows.
 *
 * ── Why the sorting is a DECISION and not a render ───────────────────────────────────────────
 * „Which band does this question belong in" is a statement about the domain, not about CSS: a
 * two-option single choice is a READING because its answer is one word out of two, a three-option
 * one reads as prose because it is not a quantity, and a question the occurrence was never asked
 * belongs in no band at all. The component that draws four sections would otherwise hold four
 * filters over the same array, and a fifth kind of answer would have to be added to all of them.
 *
 * ── An `absent` row is not in the report ─────────────────────────────────────────────────────
 * Nobody was asked, so there is nothing to report. A row asked and UNANSWERED stays, because that
 * is an answer of its own — the distinction `SurveyCellView.state` exists for.
 *
 * Pure: no React, no DOM, no query hook, no i18next import. Everything it needs was already decided
 * by `buildSurveyHistory`; this picks a column out of that view and groups its rows.
 */

import type {
  SurveyCellView,
  SurveyHistoryView,
  SurveyRowKind,
  SurveyRowView,
} from './surveyHistory';

export interface SurveyReportEntry {
  readonly key: string;
  /** What the band prints as this entry's name — a row's label, a tile's eyebrow, a block's heading. */
  readonly label: string;
  readonly kind: SurveyRowKind;
  /**
   * The client could not have sent the form without answering this. Carried onto the entry so the
   * band draws the lock beside the label without reaching back into the row — owner, 17 September
   * 2026: the grey required mark shows in the READ of a filled survey, not only in the editor.
   */
  readonly required: boolean;
  readonly cell: SurveyCellView;
}

export interface SurveyReportMeasurement {
  /** „komplet" / „pierwszy" / „7 z 10" — the muted aside after the band's own title. */
  readonly caption: string | null;
  readonly fields: readonly SurveyReportEntry[];
}

export interface SurveyReportView {
  /**
   * Every quantity the occurrence carries, one per row: a number, a 1–5 rating, a yes/no and a
   * two-option single choice. One word out of two IS a reading — what a trainer does with it is
   * compare it to last week, which is exactly what the row's third track prints.
   */
  readonly readings: readonly SurveyReportEntry[];
  /** The composite body measurement, or `null` when this occurrence was not asked for one. */
  readonly measurement: SurveyReportMeasurement | null;
  /** The questions answered in words — free text, a date, a choice of three or more. */
  readonly answers: readonly SurveyReportEntry[];
  /** The photographs and the clip. They share one band, because they are one act by the client. */
  readonly media: readonly SurveyReportEntry[];
}

/** A reading is a quantity, and one word out of two is a quantity. Everything else reads as prose. */
function isReading(row: SurveyRowView): boolean {
  return row.kind === 'number' || row.kind === 'scale' || row.kind === 'boolean' ||
    row.binaryOptions !== null;
}

const MEDIA_KINDS: ReadonlySet<SurveyRowKind> = new Set(['photos', 'clip']);

export function buildSurveyReport(
  history: SurveyHistoryView,
  columnId: number,
): SurveyReportView | null {
  const index = history.columns.findIndex((column) => column.id === columnId);
  const column = history.columns[index];
  if (column === undefined) return null;

  const entryOf = (row: SurveyRowView, label: string): SurveyReportEntry | null => {
    const cell = row.cells[index];
    // `absent` — nobody was asked — is the one state that leaves the report entirely.
    return cell === undefined || cell.state === 'absent'
      ? null
      : { key: row.key, label, kind: row.kind, required: row.required, cell };
  };

  const readings: SurveyReportEntry[] = [];
  const answers: SurveyReportEntry[] = [];
  const media: SurveyReportEntry[] = [];
  const fields: SurveyReportEntry[] = [];
  let measurementCaption: string | null = null;
  let hasMeasurement = false;

  for (const row of history.rows) {
    // A value of the composite measurement is named in FULL („Talia"), not by a short form it has
    // none of; every other band prints the question's one or two words, because a tile's eyebrow
    // and a reading row's label column cannot carry a sentence.
    const entry = entryOf(row, row.sub ? row.label : row.shortLabel);
    if (entry === null) continue;

    if (row.sub) {
      fields.push(entry);
      continue;
    }
    if (row.kind === 'measurementGroup') {
      hasMeasurement = true;
      // The group's own cell is the word for how complete the set is — „komplet", „pierwszy",
      // „7 z 10" — decided in `buildRows` and printed here as the band's aside.
      measurementCaption = entry.cell.text;
      continue;
    }
    if (MEDIA_KINDS.has(row.kind)) {
      media.push(entry);
      continue;
    }
    if (isReading(row)) {
      readings.push(entry);
      continue;
    }
    // A question the trainer asked in full sentences, named in full: a heading over a paragraph has
    // the room, and „Notatki" would lose which notes were asked for.
    answers.push({ ...entry, label: row.label });
  }

  return {
    readings,
    measurement: hasMeasurement ? { caption: measurementCaption, fields } : null,
    answers,
    media,
  };
}

/**
 * The readings a SMALL task card has room for (owner, 25 September 2026: three rows, no more) — the
 * ones that moved most against the previous answer, a reading with nothing to compare to after every
 * one that has. Ties keep the form's own order, so two equal changes read as the client answered them.
 */
export function readingsByChange(
  readings: readonly SurveyReportEntry[],
  limit: number,
): readonly SurveyReportEntry[] {
  const moved = (entry: SurveyReportEntry): number =>
    entry.cell.delta === null ? -1 : Math.abs(entry.cell.delta);
  return readings
    .map((entry, order) => ({ entry, order }))
    .sort((left, right) => moved(right.entry) - moved(left.entry) || left.order - right.order)
    .slice(0, limit)
    .map(({ entry }) => entry);
}
